import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

// Lip-persistent mouth: blends the approved frames by moving their shapes (flow morph), never by fading lips out.
// Tile 0 of each atlas is the master photo; weights cover tiles 1..7 and the master takes the remainder.
// The flow atlas has one extra tile: a unit jaw drop (lower lip + chin + lower cheeks move together).
// Box, scale and per-position jaw amounts come from /coaches/<who>_rig.json (built by backend/scripts).
const TILES = 8;
const VS = "attribute vec2 a;varying vec2 v;void main(){v=vec2(a.x*.5+.5,.5-a.y*.5);gl_Position=vec4(a,0.,1.);}";
const FS = `precision highp float;varying vec2 v;uniform sampler2D img,flo;uniform float w[${TILES}];uniform vec2 sz;uniform float sc,jaw;
vec2 tuv(float k,vec2 p,float n){p=clamp(p,vec2(.5),sz-.5);return vec2((k*sz.x+p.x)/(sz.x*n),p.y/sz.y);}
vec2 F(float k,vec2 p){return (texture2D(flo,tuv(k,p,${TILES + 1}.)).rg*255.-128.)*sc;}
void main(){vec2 p=v*sz;vec2 D=vec2(0.);float t=0.;
for(int i=0;i<${TILES};i++){D+=w[i]*F(float(i),p);t+=w[i];}
D=D/t+jaw*F(${TILES}.,p);vec3 c=vec3(0.);
for(int i=0;i<${TILES};i++){if(w[i]>.001){float k=float(i);c+=w[i]*texture2D(img,tuv(k,p+D-F(k,p),${TILES}.)).rgb;}}
c/=t;vec2 q=(v-.5)*2.;float a=clamp((1.-length(q))/.25,0.,1.);gl_FragColor=vec4(c*a,a);}`;

const tex = (gl, unit, im) => {
  const t = gl.createTexture();
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, im);
  [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach((k) => gl.texParameteri(gl.TEXTURE_2D, k, gl.CLAMP_TO_EDGE));
  [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER].forEach((k) => gl.texParameteri(gl.TEXTURE_2D, k, gl.LINEAR));
};

const setup = (canvas, imgEl, floEl, rig) => {
  const gl = canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true });
  if (!gl) return null;
  const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
  const pr = gl.createProgram();
  gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(pr);
  if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) { console.warn("mouth-rig: shader failed", gl.getProgramInfoLog(pr)); return null; }
  gl.useProgram(pr);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(pr, "a");
  gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  tex(gl, 0, imgEl); tex(gl, 1, floEl);
  gl.uniform1i(gl.getUniformLocation(pr, "img"), 0); gl.uniform1i(gl.getUniformLocation(pr, "flo"), 1);
  gl.uniform2f(gl.getUniformLocation(pr, "sz"), rig.box[2], rig.box[3]);
  gl.uniform1f(gl.getUniformLocation(pr, "sc"), rig.scale);
  const wl = gl.getUniformLocation(pr, "w");
  const jl = gl.getUniformLocation(pr, "jaw");
  return (speech, warm) => {
    const talk = Math.min(1, speech.reduce((a, x) => a + x, 0));
    const weights = [...speech, (rig.last === "rest" ? 1 - warm : warm) * (1 - talk)];
    const all = new Float32Array(TILES);
    const sum = weights.reduce((s, x) => s + x, 0);
    let jaw = 0;
    weights.forEach((x, i) => { all[i + 1] = sum > 1 ? x / sum : x; jaw += all[i + 1] * (rig.jaw[i] || 0); });
    all[0] = Math.max(0, 1 - Math.min(1, sum));
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform1fv(wl, all);
    gl.uniform1f(jl, jaw);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };
};

const loadImg = (src) => new Promise((ok, no) => { const i = new Image(); i.onload = () => ok(i); i.onerror = no; i.src = src; });

export const MouthRig = forwardRef(({ who, version }, ref) => {
  const [rig, setRig] = useState(null);
  const canvas = useRef(null);
  const draw = useRef(null);
  const last = useRef("");
  useImperativeHandle(ref, () => ({
    set: (speech, warm) => {
      const k = speech.map((x) => x.toFixed(3)).join(",") + warm.toFixed(3);
      if (draw.current && k !== last.current) { last.current = k; draw.current(speech, warm); }
    },
  }));
  useEffect(() => {
    let off = false;
    fetch(`/coaches/${who}_rig.json?${version}`).then((r) => r.json()).then((m) => { if (!off) setRig(m); }).catch(() => {});
    return () => { off = true; };
  }, [who, version]);
  useEffect(() => {
    if (!rig) return undefined;
    let off = false;
    Promise.all([loadImg(`/coaches/${who}_rig_img.jpg?${version}`), loadImg(`/coaches/${who}_rig_flow.png?${version}`)])
      .then(([a, b]) => { if (!off && canvas.current) { draw.current = setup(canvas.current, a, b, rig); last.current = ""; canvas.current.dataset.ready = draw.current ? "1" : "0"; } })
      .catch(() => {});
    return () => { off = true; };
  }, [who, version, rig]);
  if (!rig) return null;
  const [x, y, w, h] = rig.box;
  const [imgW, imgH] = rig.size;
  return (
    <canvas ref={canvas} width={w} height={h} aria-hidden data-testid="coach-portrait-mouth-rig" className="absolute select-none pointer-events-none"
      style={{ left: `${(x / imgW) * 100}%`, top: `${(y / imgH) * 100}%`, width: `${(w / imgW) * 100}%`, height: `${(h / imgH) * 100}%` }} />
  );
});
